// THE PUBLIC HOME PAGE OF THE TENANT APP — what every care provider lands on
// at "/" with no session.
//
// REBUILT 22 Sep 2026 (owner). The page it replaces spoke to PATIENTS: "Your
// Health, Our Priority", an "I am a Patient" card, three invented testimonials,
// a HIPAA badge, "join thousands", "24/7 support". None of that belonged here.
// Patients have a site of their own now (VITE_PATIENT_WEB_URL); this one is
// B2B, and its audience is EVERY kind of care or wellness provider — hospitals,
// clinics, solo doctors, telemedicine practices, pharmacies, laboratories,
// collection centres, imaging and diagnostic centres, home-care agencies,
// nursing homes, ambulance services and fitness studios.
//
// TWO RULES THE CONTENT OBEYS
//
//  1. NO FABRICATED ANYTHING. No testimonials, no customer counts, no ratings,
//     no "worldwide", no certification claims. Every sentence below is
//     something the platform actually does. The two modules that do not exist
//     (ambulance, on hold; fitness studios, no module at all) carry a gold
//     "Coming soon" badge instead of a promise.
//  2. NOTHING IS HARD-CODED IN ENGLISH. Every string is a key in
//     public/locales/{en,fr}.json under `home`; src/test/localeKeys.test.js
//     holds the two bundles against each other.
//
// SHAPE. Full-bleed bands that alternate white / #f8fefD / soft teal / ink,
// each with an inner max-w-7xl container — the visual language this page
// already had, and the one Frontend-patient-web's Landing.jsx was built to
// match. The nav and the footer were lifted into src/components/public/ so the
// four static pages wear exactly the same chrome.
//
// WHAT IT ASKS THE SERVER FOR. Only three things, all unauthenticated: the
// language master (PublicNav), the auth.*Registration feature flags (App.jsx
// boots them) and the public subscription packages (LandingPackages). There is
// no fourth call to add — and note that ANY failed request turns the whole app
// into <NetworkErrorPage/> via useAxiosAuth's interceptor, so a new call here
// is a new way for this page to go blank.

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Check, ChevronDown, Stethoscope, Pill, FlaskConical,
  ScanLine, HandHeart, Ambulance, Hospital, Building2, UserRound, Video,
  TestTubes, Microscope, HeartPulse, BedDouble, Dumbbell, Network,
  CalendarClock, FileText, Users, ShieldCheck, Wallet, Languages as LanguagesIcon,
  Sparkles, MessageSquare,
} from 'lucide-react';

import useDoctorRegistrationOpen from '../../hooks/useDoctorRegistrationOpen';
import { PATIENT_WEB_URL } from '../../utils/patientWebUrl';
import LandingPackages from '../../components/subscription/LandingPackages';
import ContactModal from '../../components/subscription/ContactModal';
import PublicNav from '../../components/public/PublicNav';
import PublicFooter from '../../components/public/PublicFooter';
import SupportContactNote from '../../components/support/SupportContactNote';

import consultationImg from '../../assets/img/consultation.webp';
import selectDateTimeImg from '../../assets/img/select-date-time.webp';
import videoCallImg from '../../assets/img/video-call.webp';
import familyImg from '../../assets/img/family.webp';
import profileSetupImg from '../../assets/img/profile-setup.webp';
import securePaymentImg from '../../assets/img/secure-payment.webp';
import fileUploadImg from '../../assets/img/file-upload.webp';
import settingsPrivacyImg from '../../assets/img/settings-privacy.webp';
import discoverDoctorsImg from '../../assets/img/discover-doctors.webp';

// ── layout primitives ───────────────────────────────────────────────────────

// Reveals are decoration. `useReducedMotion` turns them off for anybody whose
// OS asked for less movement, and `?static=1` turns them off for screenshot
// runs — a headless capture must never disagree with what a person sees.
const STATIC = typeof window !== 'undefined'
  && typeof window.location?.search === 'string'
  && window.location.search.includes('static=1');

// Aliased out of the namespace deliberately: this project's eslint has no react
// plugin, so a binding used ONLY as <motion.div> reads as unused.
const MotionDiv = motion.div;

function Reveal({ children, className = '', delay = 0, y = 18 }) {
  const reduced = useReducedMotion();
  if (reduced || STATIC) return <div className={className}>{children}</div>;
  return (
    <MotionDiv
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
    >
      {children}
    </MotionDiv>
  );
}

/** One full-bleed band: the background is the section, the content is capped. */
function Band({ id, className = '', children, testId }) {
  return (
    <section id={id} data-testid={testId} className={`home-anchor w-full ${className}`}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

/** The centred eyebrow + gradient heading + sub that most bands share. */
function BandHead({ eyebrow, title, sub }) {
  return (
    <Reveal className="mx-auto max-w-3xl text-center">
      <span className="mb-3 inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#2D8A8A]">
        {eyebrow}
      </span>
      <h2 className="gradient-heading">{title}</h2>
      <p className="mt-2 text-base leading-relaxed text-gray-600 sm:text-lg">{sub}</p>
    </Reveal>
  );
}

// EVERY ILLUSTRATION IS A WEBP WITH A BAKED-IN WHITE BACKGROUND, so dropped
// onto a tinted band it reads as a pasted white card with hard edges.
// `mix-blend-multiply` makes that white disappear into whatever is behind it
// (white x anything = anything); `isolate` keeps the blend inside this group so
// it can never reach the page background.
function Figure({ src, alt, panel = '', className = '', pad = 'p-8' }) {
  return (
    <div className={`relative isolate mx-auto w-full ${className}`}>
      {panel && <span className={`absolute inset-0 ${panel}`} />}
      <img src={src} alt={alt} loading="lazy" className={`relative w-full mix-blend-multiply ${pad}`} />
    </div>
  );
}

/** The 64px gradient square with a white glyph this page uses everywhere. */
function IconTile(props) {
  // Destructured in the BODY, not the signature: an unused-looking argument is
  // an eslint error here, while a capitalised local is ignored by config.
  const { Icon, size = 'lg' } = props;
  const box = size === 'lg' ? 'h-16 w-16' : 'h-12 w-12';
  const glyph = size === 'lg' ? 'h-8 w-8' : 'h-6 w-6';
  return (
    <span className={`${box} flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] shadow-sm`}>
      <Icon className={`${glyph} text-white`} strokeWidth={1.8} />
    </span>
  );
}

// ── content tables ──────────────────────────────────────────────────────────
// Keys, never sentences — the page re-renders in French without re-mounting.

// 2 · SOLUTIONS. The wording follows Backend/scripts/seed.js's tenant-type
// master wherever the two describe the same establishment, so a visitor reads
// the same names the Super Admin picks from.
const SOLUTIONS = [
  { key: 'hospital', Icon: Hospital },
  { key: 'clinic', Icon: Building2 },
  { key: 'solo', Icon: UserRound },
  { key: 'telemedicine', Icon: Video },
  { key: 'pharmacy', Icon: Pill },
  { key: 'lab', Icon: FlaskConical },
  { key: 'collection', Icon: TestTubes },
  { key: 'radiology', Icon: ScanLine },
  { key: 'diagnostic', Icon: Microscope },
  { key: 'homecare', Icon: HandHeart },
  { key: 'nursing', Icon: BedDouble },
  // No module exists for either: ambulance is on hold (Backend's service
  // catalogue marks it `status: 'hold'`) and fitness has never been built.
  { key: 'ambulance', Icon: Ambulance, soon: true },
  { key: 'fitness', Icon: Dumbbell, soon: true },
];

// 3 · SERVICES. `available` mirrors Backend/config/tenantFeatures.js's
// TENANT_SERVICES — five sellable, ambulance on hold — rather than a second
// hand-kept opinion about what has shipped.
const SERVICES = [
  { key: 'medi_consult', Icon: Stethoscope, available: true },
  { key: 'pharma', Icon: Pill, available: true },
  { key: 'lab', Icon: FlaskConical, available: true },
  { key: 'radiology', Icon: ScanLine, available: true },
  { key: 'care_taker', Icon: HandHeart, available: true },
  { key: 'ambulance', Icon: Ambulance, available: false },
];

// 4 · FEATURES — alternating illustration + copy rows.
const FEATURES = [
  { key: 'scheduling', Icon: CalendarClock, img: selectDateTimeImg },
  { key: 'video', Icon: Video, img: videoCallImg },
  { key: 'records', Icon: FileText, img: fileUploadImg },
  { key: 'identity', Icon: Users, img: familyImg },
  { key: 'team', Icon: MessageSquare, img: profileSetupImg },
  { key: 'billing', Icon: Wallet, img: securePaymentImg },
  { key: 'security', Icon: ShieldCheck, img: settingsPrivacyImg },
  { key: 'language', Icon: LanguagesIcon, img: discoverDoctorsImg },
];

const STEPS = ['step1', 'step2', 'step3', 'step4'];
const FAQ_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
const CUSTOM_POINTS = ['custom_point1', 'custom_point2', 'custom_point3', 'custom_point4'];

const SOON_BADGE = 'rounded-md border border-[#D7A05C]/40 bg-[#D7A05C]/10 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-[#B07C36]';

// ── the page ────────────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('home');
  // Shown when the doctor registration page can offer an organisation — the
  // host's, or any that allows voluntary sign-up (hooks/useDoctorRegistrationOpen).
  // The platform flag no longer decides it ("Concept 2", 1 Oct 2026).
  const { open: doctorRegEnabled } = useDoctorRegistrationOpen();
  const hasPatientSite = Boolean(PATIENT_WEB_URL);

  // "Talk to us" opens the same contact form the pricing cards use. It is NOT
  // package-bound — ContactModal renders "a custom plan" when `pkg` is null —
  // so the hero, the custom-bundle card and the closing band all share it.
  const [contactOpen, setContactOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // A section link pressed from /about (or any other static page) navigates
  // here carrying the target; this performs the scroll once the band exists.
  useEffect(() => {
    const id = location.state?.scrollTo;
    if (!id) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [location.state]);

  const talkToUs = () => setContactOpen(true);

  return (
    <div className="w-full bg-white" data-testid="home-container">
      <PublicNav />

      {/* pt-16 clears the fixed bar so no band has to know its height. */}
      <main className="pt-16">

        {/* ══ 1 · HERO ═══════════════════════════════════════════════════════ */}
        <section
          className="relative w-full overflow-hidden bg-gradient-to-br from-[#f0faf9] via-white to-[#f0faf9]"
          data-testid="home-hero"
        >
          <span className="pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-[#4ABDBD]/10 blur-3xl" />
          <span className="pointer-events-none absolute -bottom-52 -left-32 h-96 w-96 rounded-full bg-[#D7A05C]/10 blur-3xl" />

          <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-4 pb-24 pt-14 sm:px-6 sm:pb-28 sm:pt-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:px-8 lg:pt-24">
            <Reveal y={24}>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#4ABDBD]/30 bg-white/80 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#2D8A8A] shadow-sm backdrop-blur-sm sm:text-xs">
                <HeartPulse size={14} /> {t('home.hero_eyebrow')}
              </span>

              <h1 className="gradient-heading mt-5 max-w-2xl" data-testid="home-hero-title">
                {t('home.hero_title_a')} <span className="heading-accent">{t('home.hero_title_b')}</span>
              </h1>

              <p className="max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg" data-testid="home-hero-lead">
                {t('home.hero_lead')}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <button
                  type="button"
                  onClick={talkToUs}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-7 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-[#4ABDBD]/25 transition-all hover:shadow-xl sm:w-auto"
                  data-testid="home-hero-contact"
                >
                  {t('home.hero_cta_contact')} <ArrowRight size={17} />
                </button>
                {/* Hidden outright when doctor sign-up is off — the route
                    itself bounces home in that case, so a button that leads
                    there is a dead end. */}
                {doctorRegEnabled && (
                  <button
                    type="button"
                    onClick={() => navigate('/doctor-register')}
                    className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-[#4ABDBD] bg-white px-7 py-3.5 text-[15px] font-bold text-[#2D8A8A] transition-colors hover:bg-[#4ABDBD]/10 sm:w-auto"
                    data-testid="home-hero-solo"
                  >
                    <Stethoscope size={17} /> {t('home.hero_cta_solo')}
                  </button>
                )}
              </div>

              <ul className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="home-hero-trust">
                {['hero_trust1', 'hero_trust2', 'hero_trust3'].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-sm font-medium text-gray-600">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#4ABDBD]/15">
                      <Check size={12} className="text-[#2D8A8A]" strokeWidth={3} />
                    </span>
                    {t(`home.${k}`)}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal y={24} delay={0.1} className="relative hidden lg:block">
              <img
                src={consultationImg}
                alt={t('home.hero_illustration_alt')}
                className="home-float-slow mx-auto w-full max-w-lg mix-blend-multiply"
              />
              <span className="home-float absolute left-0 top-8 flex items-center gap-2.5 rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-xl backdrop-blur-md">
                <IconTile Icon={Video} size="sm" />
                <span className="text-[13px] font-bold leading-tight text-gray-800">
                  {t('home.hero_chip1_title')}
                  <span className="block text-[11px] font-medium text-gray-500">{t('home.hero_chip1_sub')}</span>
                </span>
              </span>
              <span className="home-float home-float-delay absolute bottom-24 right-0 flex items-center gap-2.5 rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-xl backdrop-blur-md">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#D7A05C] to-[#c08b45] shadow-sm">
                  <CalendarClock className="h-6 w-6 text-white" strokeWidth={1.8} />
                </span>
                <span className="text-[13px] font-bold leading-tight text-gray-800">
                  {t('home.hero_chip2_title')}
                  <span className="block text-[11px] font-medium text-gray-500">{t('home.hero_chip2_sub')}</span>
                </span>
              </span>
              <span className="home-float absolute -bottom-2 left-8 flex items-center gap-2.5 rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-xl backdrop-blur-md">
                <IconTile Icon={Users} size="sm" />
                <span className="text-[13px] font-bold leading-tight text-gray-800">
                  {t('home.hero_chip3_title')}
                  <span className="block text-[11px] font-medium text-gray-500">{t('home.hero_chip3_sub')}</span>
                </span>
              </span>
            </Reveal>
          </div>
        </section>

        {/* ══ 2 · SOLUTIONS ══════════════════════════════════════════════════ */}
        <Band id="solutions" testId="home-solutions" className="bg-white py-20 sm:py-24 lg:py-28">
          <BandHead
            eyebrow={t('home.solutions_eyebrow')}
            title={t('home.solutions_title')}
            sub={t('home.solutions_sub')}
          />
          {/* flex, not grid: there are THIRTEEN provider types, so a 3-column
              grid strands the last tile against the left edge. A centred wrap
              makes the short final row read as deliberate. */}
          <div className="mt-14 flex flex-wrap justify-center gap-5">
            {SOLUTIONS.map((s, i) => (
              <Reveal
                key={s.key}
                delay={(i % 3) * 0.06}
                className="w-full sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]"
              >
                <article
                  data-testid={`home-solution-${s.key}`}
                  className={`flex h-full items-start gap-4 rounded-2xl border-2 p-6 transition-all ${
                    s.soon
                      ? 'border-gray-100 bg-gray-50/60'
                      : 'border-gray-100 bg-white hover:border-[#4ABDBD] hover:shadow-lg'
                  }`}
                >
                  <span
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm ${
                      s.soon
                        ? 'bg-gradient-to-br from-[#C7D6D5] to-[#9DB3B1]'
                        : 'bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]'
                    }`}
                  >
                    <s.Icon className="h-6 w-6 text-white" strokeWidth={1.8} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-gray-900">
                      {t(`home.solution_${s.key}_title`)}
                      {s.soon && (
                        <span className={SOON_BADGE} data-testid={`home-solution-${s.key}-soon`}>
                          {t('home.badge_coming_soon')}
                        </span>
                      )}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                      {t(`home.solution_${s.key}_desc`)}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <article
              data-testid="home-solution-group"
              className="mt-5 flex flex-col items-start gap-5 rounded-2xl border-2 border-[#4ABDBD]/25 bg-gradient-to-br from-[#f0faf9] to-white p-7 sm:flex-row sm:items-center sm:p-8"
            >
              <IconTile Icon={Network} />
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900">{t('home.solution_group_title')}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600">{t('home.solution_group_desc')}</p>
              </div>
            </article>
          </Reveal>
        </Band>

        {/* ══ 3 · SERVICES ═══════════════════════════════════════════════════ */}
        <Band id="services" testId="home-services" className="bg-[#f8fefD] py-20 sm:py-24 lg:py-28">
          <BandHead
            eyebrow={t('home.services_eyebrow')}
            title={t('home.services_title')}
            sub={t('home.services_sub')}
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.key} delay={(i % 2) * 0.08} className="h-full">
                <article
                  data-testid={`home-service-${s.key}`}
                  className="flex h-full gap-5 rounded-2xl border-2 border-gray-100 bg-white p-6 transition-all hover:border-[#4ABDBD] hover:shadow-lg sm:p-7"
                >
                  <IconTile Icon={s.Icon} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="text-lg font-bold text-gray-900">{t(`home.service_${s.key}_title`)}</h3>
                      <span
                        data-testid={`home-service-${s.key}-status`}
                        className={
                          s.available
                            ? 'rounded-full border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#2D8A8A]'
                            : 'rounded-full border border-[#D7A05C]/40 bg-[#D7A05C]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#B07C36]'
                        }
                      >
                        {t(s.available ? 'home.status_available' : 'home.status_preparing')}
                      </span>
                    </div>
                    <p className="mt-2.5 text-[15px] leading-relaxed text-gray-600">
                      {t(`home.service_${s.key}_desc`)}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </Band>

        {/* ══ 4 · FEATURES ═══════════════════════════════════════════════════ */}
        <Band id="features" testId="home-features" className="bg-white py-20 sm:py-24 lg:py-28">
          <BandHead
            eyebrow={t('home.features_eyebrow')}
            title={t('home.features_title')}
            sub={t('home.features_sub')}
          />
          <div className="mt-16 space-y-20 lg:space-y-24">
            {FEATURES.map((f, i) => {
              const flipped = i % 2 === 1;
              return (
                <div
                  key={f.key}
                  data-testid={`home-feature-${f.key}`}
                  className="grid items-center gap-6 lg:grid-cols-2 lg:gap-16"
                >
                  {/* ON A PHONE THE ILLUSTRATION COMES FIRST, always. With the
                      text first, each row's picture landed directly above the
                      NEXT row's heading and read as belonging to it. On lg the
                      two columns alternate side to side as usual. */}
                  <Reveal className={`order-1 ${flipped ? 'lg:order-1' : 'lg:order-2'}`}>
                    <Figure
                      src={f.img}
                      alt={t(`home.feature_${f.key}_alt`)}
                      className="max-w-md"
                      panel={`rounded-[3rem] ${flipped ? 'bg-gradient-to-br from-[#eaf7f6] to-[#f7fcfb]' : 'bg-gradient-to-tl from-[#eaf7f6] to-[#f7fcfb]'}`}
                      pad="p-10"
                    />
                  </Reveal>
                  <Reveal delay={0.08} className={`order-2 ${flipped ? 'lg:order-2' : 'lg:order-1'}`}>
                    <span className="mb-3 inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#2D8A8A]">
                      {t(`home.feature_${f.key}_eyebrow`)}
                    </span>
                    <div className="flex items-center gap-4">
                      <IconTile Icon={f.Icon} size="sm" />
                      <h3 className="text-2xl font-extrabold leading-tight text-gray-900 sm:text-3xl">
                        {t(`home.feature_${f.key}_title`)}
                      </h3>
                    </div>
                    <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">
                      {t(`home.feature_${f.key}_desc`)}
                    </p>
                    <ul className="mt-6 space-y-3">
                      {['p1', 'p2', 'p3'].map((p) => (
                        <li key={p} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15">
                            <Check size={13} className="text-[#2D8A8A]" strokeWidth={3} />
                          </span>
                          <span className="text-[15px] leading-relaxed text-gray-700">
                            {t(`home.feature_${f.key}_${p}`)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                </div>
              );
            })}
          </div>
        </Band>

        {/* ══ 5 · ONBOARDING ═════════════════════════════════════════════════ */}
        <Band id="how" testId="home-how" className="bg-[#f8fefD] py-20 sm:py-24 lg:py-28">
          <BandHead eyebrow={t('home.how_eyebrow')} title={t('home.how_title')} sub={t('home.how_sub')} />
          <ol className="relative mx-auto mt-14 max-w-3xl space-y-8">
            {/* The connector stops at the last disc rather than running past it. */}
            <span className="absolute left-6 top-4 hidden h-[calc(100%-4rem)] w-px bg-gradient-to-b from-[#4ABDBD] via-[#4ABDBD]/40 to-transparent sm:block" />
            {STEPS.map((step, i) => (
              <Reveal key={step} delay={i * 0.08}>
                <li className="relative flex gap-5" data-testid={`home-how-${step}`}>
                  <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-lg font-extrabold text-white shadow-md shadow-[#4ABDBD]/25 ring-4 ring-[#f8fefD]">
                    {i + 1}
                  </span>
                  <div className="pt-1">
                    <h3 className="text-lg font-bold text-gray-900">{t(`home.how_${step}_title`)}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600">{t(`home.how_${step}_desc`)}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </Band>

        {/* ══ 6 · FOR PATIENTS ═══════════════════════════════════════════════ */}
        {/* Only when this deployment HAS a patient site. Without a URL the
            strip would be an apology with nowhere to send anybody. */}
        {hasPatientSite && (
          <Band testId="home-for-patients" className="bg-white py-14 sm:py-16">
            <Reveal>
              <div className="flex flex-col items-start gap-6 rounded-3xl border-2 border-[#4ABDBD]/25 bg-gradient-to-br from-[#f0faf9] via-white to-[#e2f4f2] p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-5">
                  <IconTile Icon={HeartPulse} />
                  <div>
                    <h2 className="text-xl font-extrabold text-gray-900 sm:text-2xl">{t('home.patients_title')}</h2>
                    <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-gray-600">{t('home.patients_desc')}</p>
                  </div>
                </div>
                <a
                  href={`${PATIENT_WEB_URL}/#/`}
                  data-testid="home-patient-site-link"
                  className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-6 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-[#4ABDBD]/25 transition-all hover:shadow-xl"
                >
                  {t('home.patients_cta')} <ArrowUpRight size={17} />
                </a>
              </div>
            </Reveal>
          </Band>
        )}

        {/* ══ 7 · PRICING ════════════════════════════════════════════════════ */}
        {/* <LandingPackages/> owns `#pricing` and hides itself when the platform
            sells no public plan, so the nav's Pricing entry falls through to
            `#custom-plan` (see PUBLIC_SECTIONS). The two share one background
            so they read as a single band. */}
        <LandingPackages />
        <Band id="custom-plan" testId="home-custom-plan" className="bg-[#f0faf9] pb-24 pt-4 sm:pb-28">
          <Reveal>
            <div className="grid items-center gap-8 rounded-3xl border-2 border-[#4ABDBD]/25 bg-white p-7 shadow-sm sm:p-10 lg:grid-cols-[1.15fr_0.85fr]">
              <div>
                <span className="mb-3 inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#2D8A8A]">
                  {t('home.custom_eyebrow')}
                </span>
                <h3 className="text-2xl font-extrabold leading-tight text-gray-900 sm:text-3xl">
                  {t('home.custom_title')}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">{t('home.custom_desc')}</p>
              </div>
              <div>
                <ul className="space-y-3">
                  {CUSTOM_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15">
                        <Check size={13} className="text-[#2D8A8A]" strokeWidth={3} />
                      </span>
                      <span className="text-[15px] leading-relaxed text-gray-700">{t(`home.${p}`)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={talkToUs}
                  data-testid="home-custom-contact"
                  className="mt-7 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] px-7 py-3.5 text-[15px] font-bold text-white shadow-lg shadow-[#4ABDBD]/25 transition-all hover:shadow-xl"
                >
                  {t('home.custom_cta')} <ArrowRight size={17} />
                </button>
              </div>
            </div>
          </Reveal>
        </Band>

        {/* ══ 8 · FAQ ════════════════════════════════════════════════════════ */}
        <Band id="faq" testId="home-faq" className="bg-white py-20 sm:py-24 lg:py-28">
          <BandHead eyebrow={t('home.faq_eyebrow')} title={t('home.faq_title')} sub={t('home.faq_sub')} />
          <div className="mx-auto mt-12 max-w-3xl space-y-3">
            {FAQ_IDS.map((n, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={n} delay={Math.min(i, 4) * 0.04}>
                  <div className={`overflow-hidden rounded-2xl border-2 bg-white transition-colors ${open ? 'border-[#4ABDBD]' : 'border-gray-100'}`}>
                    <h3>
                      <button
                        type="button"
                        data-testid={`home-faq-q-${n}`}
                        aria-expanded={open}
                        aria-controls={`home-faq-a-${n}`}
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-5 text-left sm:px-7"
                      >
                        <span className="text-[15px] font-bold text-gray-900 sm:text-base">{t(`home.faq_q${n}`)}</span>
                        <ChevronDown
                          size={20}
                          className={`shrink-0 text-[#2D8A8A] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </h3>
                    {open && (
                      <div
                        id={`home-faq-a-${n}`}
                        data-testid={`home-faq-a-${n}`}
                        className="px-5 pb-6 text-[15px] leading-relaxed text-gray-600 sm:px-7"
                      >
                        {t(`home.faq_a${n}`)}
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Band>

        {/* ══ 9 · FINAL CTA ══════════════════════════════════════════════════ */}
        <section
          className="w-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] py-20 sm:py-24"
          data-testid="home-cta"
        >
          <div className="mx-auto w-full max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <Reveal>
              <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                <Sparkles size={14} /> {t('home.cta_eyebrow')}
              </span>
              <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
                {t('home.cta_title')}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg">
                {t('home.cta_subtitle')}
              </p>
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                <button
                  type="button"
                  onClick={talkToUs}
                  data-testid="home-cta-contact"
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 text-base font-bold text-[#2D8A8A] shadow-lg transition-all hover:shadow-xl"
                >
                  {t('home.cta_contact')} <ArrowRight size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  data-testid="home-cta-sign-in"
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-white px-8 py-4 text-base font-bold text-white transition-colors hover:bg-white hover:text-[#2D8A8A]"
                >
                  {t('home.cta_sign_in')}
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      {/* Before sign-in the in-app ticket is out of reach: the support mailbox,
          with the no-medical-records warning. */}
      <section className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <SupportContactNote variant="help" />
      </section>

      <PublicFooter />

      {contactOpen && <ContactModal source="landing" onClose={() => setContactOpen(false)} />}
    </div>
  );
}
